import React, { useState, useEffect, useMemo } from 'react';
import { useYearRange } from '../constants/yearRange';
import { supabase } from '../supabase';
import { fileUrl } from '../utils/fileUrl';
import { loadExpectedBills, workflowLabel, groupForApprovals } from '../utils/expectedBills';
import { TAX_BILL_STATUS } from '../constants/statuses';
import { rolesForClient, staffNameFrom } from '../utils/assignments';
import useAssignments from '../hooks/useAssignments';
import JurisdictionBreakdown from '../components/JurisdictionBreakdown';
import { detectRateUnit } from '../reports/taxRate';
import { moneyNum } from '../utils/money';
import { paymentSummary } from '../components/BillPayment';
import { useAccountFallback, resolveAccount, accountTitle } from '../utils/accountFallback';

// CS-editable fields on the approval Review panel — the error-prone financial/date figures (identity fields
// like payee/collector are re-do territory → Issue, not an inline fix). Money parses through the tolerant
// moneyNum; the tax RATE must NOT (a legit 1.302 would collapse), so it uses a plain decimal parse.
const rateParse = (v) => { const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, '')); return isNaN(n) ? null : n; };
const CS_EDIT_FIELDS = [
  { key: 'total_bill_amount',    label: 'Amount Due (this bill)', type: 'money' },
  { key: 'total_ad_valorem',     label: 'Ad Valorem (basis)',     type: 'money' },
  { key: 'total_non_ad_valorem', label: 'Non-Ad-Valorem',         type: 'money' },
  { key: 'discount_amount',      label: 'Discount',               type: 'money' },
  { key: 'penalty_fees',         label: 'Penalty / Fees',         type: 'money' },
  { key: 'taxable_value',        label: 'Taxable Value',          type: 'money' },
  { key: 'market_value',         label: 'Market Value',           type: 'money' },
  { key: 'total_tax_rate',       label: 'Tax Rate',               type: 'rate'  },
  { key: 'due_date',             label: 'Due Date',               type: 'date'  },
  { key: 'discount_date',        label: 'Discount Date',          type: 'date'  },
];
const parseField = (f, raw) => f.type === 'money' ? moneyNum(raw) : f.type === 'rate' ? rateParse(raw) : ((raw || '') || null);

// Tax Bill Approvals (Bubble 2, CS side). Queue = properties with ≥1 bill awaiting CS approval (workflow_status
// 'processed'). Nested: property → (collector, installment) sub-card → parcels, each with a status chip. All expected
// parcels show (incl. not_started/outstanding) for "waiting on 3 of 4" context. The logged-in CS's properties sort
// to top. Orphans (a bill with no collector selection) surface here with the data-gap indicator (their real home).
// C2a: per-parcel Approve + sub-card Approve-all; a blocking File/Transmit fork for non-transmit clients; per-parcel
// Issue (comment → status='rejected' → back to the TS worklist). Actions only on 'processed' rows with a tax_bill_id.

const parseLocal = (s) => { if (!s) return null; const [y, m, d] = String(s).split('-').map(Number); return (y && m && d) ? new Date(y, m - 1, d) : null; };
const fmtDate = (s) => { const d = parseLocal(s); return d ? d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'; };
const currentTaxYear = () => sessionStorage.getItem('wh_valueYear') || String(new Date().getFullYear());

// Status chip palette by workflow_status.
const chip = (ws, isDark) => {
  const map = {
    approved:    ['#059669', 'rgba(5,150,105,0.12)'],
    processed:   [isDark ? '#fbbf24' : '#8A5B00', 'rgba(251,191,36,0.15)'],
    rejected:    ['#B40000', 'rgba(180,0,0,0.10)'],
    transmitted: ['#0e7490', 'rgba(14,116,144,0.12)'],
    filed:       ['#0e7490', 'rgba(14,116,144,0.12)'],
    not_started: [isDark ? '#a8b0cc' : '#4a5068', 'rgba(120,128,160,0.12)'],
    outstanding: [isDark ? '#a8b0cc' : '#4a5068', 'rgba(120,128,160,0.12)'],
  };
  const [color, bg] = map[ws] || map.outstanding;
  return { color, background: bg, fontSize: '11px', fontWeight: '700', borderRadius: '6px', padding: '2px 8px', whiteSpace: 'nowrap' };
};

export default function TaxBillApprovals({ darkMode, staffUser, onMutated }) {
  // Year selector range ← folder_year_config (Admin → Year Management), one source for every
  // value-year picker in the app. Falls back to 2021-2028 if the config row can't be read.
  const { years } = useYearRange();
  const isDark = darkMode;
  const c = isDark
    ? { card: 'rgba(47,56,84,0.6)', border: '#374060', text: '#e8eaf2', muted: 'var(--slate)', sub: '#a8b0cc', input: 'var(--ink)', inputBorder: '#434C68', subCard: 'rgba(26,34,56,0.5)', rowBorder: 'rgba(67,76,104,0.25)', mine: 'rgba(180,0,0,0.35)' }
    : { card: '#ffffff', border: 'var(--line)', text: 'var(--ink)', muted: 'var(--slate)', sub: '#4a5068', input: '#ffffff', inputBorder: '#c8ccd8', subCard: '#f7f7fb', rowBorder: '#eef0f6', mine: 'var(--red)' };
  const fontStack = "'Public Sans',system-ui,sans-serif";
  const s = {
    input: { width: '100%', padding: '8px 11px', background: c.input, border: `1px solid ${c.inputBorder}`, borderRadius: '8px', color: c.text, fontSize: '13px', outline: 'none', boxSizing: 'border-box', fontFamily: fontStack },
    btnApprove: { padding: '4px 11px', background: '#059669', border: 'none', borderRadius: '7px', color: '#fff', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: fontStack, whiteSpace: 'nowrap' },
    btnIssue: { padding: '4px 11px', background: 'transparent', border: `1px solid ${isDark ? 'rgba(180,0,0,0.5)' : 'rgba(180,0,0,0.4)'}`, borderRadius: '7px', color: isDark ? '#fca5a5' : 'var(--red)', fontSize: '11px', fontWeight: '700', cursor: 'pointer', fontFamily: fontStack, whiteSpace: 'nowrap' },
    btnGhost: { padding: '7px 12px', background: 'transparent', border: `1px solid ${c.inputBorder}`, borderRadius: '8px', color: c.sub, fontSize: '12px', fontWeight: '600', cursor: 'pointer', fontFamily: fontStack },
    btnPrimary: { padding: '9px 16px', background: 'linear-gradient(135deg,#B40000,#9C0000)', border: 'none', borderRadius: '8px', color: '#fff', fontSize: '13px', fontWeight: '700', cursor: 'pointer', fontFamily: fontStack },
  };
  const disabled = (st) => ({ ...st, opacity: 0.5, cursor: 'default' });

  const [year, setYear] = useState(currentTaxYear);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const { assignments, staff } = useAssignments();

  useEffect(() => {
    let alive = true;
    setLoading(true);
    loadExpectedBills(year).then(data => { if (alive) { setRows(data); setLoading(false); } });
    return () => { alive = false; };
  }, [year]);
  // Account Number: collector link → that year's parcel record → the parcel (utils/accountFallback; display only).
  const acctFb = useAccountFallback(rows, year);

  // The property's client's CS (per-client, not state-scoped) → id + name + is-this-me.
  const csFor = useMemo(() => (clientId) => {
    const id = rolesForClient(assignments, clientId, 'CS')[0]?.staff_id || null;
    return { id, name: id ? staffNameFrom(staff, id) : '', mine: !!id && String(id) === String(staffUser?.id) };
  }, [assignments, staff, staffUser]);

  const groups = useMemo(() => groupForApprovals(rows), [rows]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = groups;
    if (q) list = list.filter(g => [g.property_name, g.client_name, g.state, ...g.subs.map(su => su.collector_name)].some(v => (v || '').toLowerCase().includes(q)));
    // My properties (as CS) first, then property name.
    return [...list].sort((a, b) => {
      const am = csFor(a.client_id).mine ? 0 : 1, bm = csFor(b.client_id).mine ? 0 : 1;
      if (am !== bm) return am - bm;
      return (a.property_name || '').localeCompare(b.property_name || '');
    });
  }, [groups, search, csFor]);

  // ── Mutations (C2a) ───────────────────────────────────────────────────────
  const [busy, setBusy] = useState(false);
  const [fileFork, setFileFork] = useState(null); // { ids:[tax_bill_id...] } — non-transmit approve → blocking File/Transmit choice
  const [issueFor, setIssueFor] = useState(null); // the tax_bill_id whose Issue comment box is open
  const [issueText, setIssueText] = useState('');
  const [openBill, setOpenBill] = useState(null);  // tax_bill_id whose Review panel is expanded
  const [billData, setBillData] = useState({});     // { [tax_bill_id]: full tax_bills row + pdfUrl + tsName } (cached)
  // Paid / review state per bill, for the row tags (expected_bills does not carry it). Chunked reads of the ids shown.
  const [billFlags, setBillFlags] = useState({});   // { [tax_bill_id]: { paid, payment, needsReview, reviewNote } }
  useEffect(() => {
    const ids = [...new Set(rows.map(r => r.tax_bill_id).filter(Boolean))];
    if (!ids.length) { setBillFlags({}); return undefined; }
    let alive = true;
    (async () => {
      const out = {};
      for (let i = 0; i < ids.length; i += 100) {
        const { data, error } = await supabase.from('tax_bills').select('id,paid_source,paid_at,paid_amount,paid_proof,paid_note,needs_review,review_note').in('id', ids.slice(i, i + 100));
        if (error) { console.warn('[approvals] paid flags:', error.message); break; }   // tags are informational — never block the screen
        (data || []).forEach(b => { out[b.id] = { paid: !!b.paid_source, payment: b.paid_source ? { source: b.paid_source, at: b.paid_at, amount: b.paid_amount, proof: b.paid_proof, note: b.paid_note } : null, needsReview: !!b.needs_review, reviewNote: b.review_note }; });
      }
      if (alive) setBillFlags(out);
    })();
    return () => { alive = false; };
  }, [rows]);
  const [toast, setToast] = useState(null);         // transient confirmation banner stating an action's outcome
  const [editVals, setEditVals] = useState(null);   // {col: string} while the CS is inline-editing the open panel; null = view mode
  useEffect(() => { if (!toast) return; const t = setTimeout(() => setToast(null), 4000); return () => clearTimeout(t); }, [toast]);

  // Review panel: the CS's second check. Fetch the bill's TS-confirmed extraction + a signed PDF URL on expand.
  const money = (v) => v == null || v === '' ? '—' : '$' + Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  async function toggleReview(tbId) {
    setEditVals(null);                                // collapsing/switching panels drops any unsaved edit
    if (openBill === tbId) { setOpenBill(null); return; }
    setOpenBill(tbId);
    if (billData[tbId]) return;                       // already cached
    const { data: b } = await supabase.from('tax_bills').select('*').eq('id', tbId).single();
    let pdfUrl = null, pdfName = null;
    if (b?.file_exchange_id) {
      const { data: fe } = await supabase.from('file_exchange').select('storage_path,file_name').eq('id', b.file_exchange_id).single();
      if (fe?.storage_path) {
        pdfUrl = await fileUrl(fe.storage_path, 'preview').catch(() => null); pdfName = fe.file_name || null;
      }
    }
    const tsName = b?.submitted_by ? staffNameFrom(staff, b.submitted_by) : null;
    setBillData(prev => ({ ...prev, [tbId]: { ...b, pdfUrl, pdfName, tsName } }));
  }

  // CS inline-edit: seed the input values from the current row (money/rate as plain number strings, dates as YYYY-MM-DD).
  function startEdit(d) {
    const v = {};
    CS_EDIT_FIELDS.forEach(f => { v[f.key] = d[f.key] == null ? '' : String(d[f.key]); });
    setEditVals(v);
  }
  // Persist the CS's corrections: diff vs current, write only changed columns + audit. cs_edits keeps the ORIGINAL
  // TS value as `from` across repeat edits. Silent-write guarded (RLS-off today, but check {error} + affected rows).
  async function saveEdit(tbId) {
    const d = billData[tbId];
    if (!d || !editVals) return;
    const changes = {}, delta = {};
    for (const f of CS_EDIT_FIELDS) {
      const cur = d[f.key] ?? null;
      const next = parseField(f, editVals[f.key]);
      const same = f.type === 'date'
        ? String(cur ?? '') === String(next ?? '')
        : (cur == null && next == null) || Number(cur) === Number(next);
      if (same) continue;
      changes[f.key] = next;
      const prior = (d.cs_edits || {})[f.key];         // preserve the ORIGINAL TS value on a repeat edit
      delta[f.key] = { from: prior ? prior.from : cur, to: next };
    }
    if (!Object.keys(changes).length) { setEditVals(null); return; }   // nothing actually changed
    setBusy(true);
    const cs_edits = { ...(d.cs_edits || {}), ...delta };
    const edited_at = new Date().toISOString();
    const { data: upd, error } = await supabase.from('tax_bills')
      .update({ ...changes, edited_by: staffUser?.id || null, edited_at, cs_edits })
      .eq('id', tbId).select('id');
    setBusy(false);
    if (error) { setToast(`Save failed — ${error.message}`); return; }
    if (!upd || upd.length === 0) { setToast('Save failed — no rows updated (access?)'); return; }   // silent-write guard
    setBillData(prev => ({ ...prev, [tbId]: { ...prev[tbId], ...changes, edited_by: staffUser?.id || null, edited_at, cs_edits } }));
    setEditVals(null);
    setToast(`Saved ${Object.keys(changes).length} CS correction${Object.keys(changes).length > 1 ? 's' : ''}`);
  }

  // Re-derive locally AND bump the dashboard counts (CS + TS: an Issue moves a bill to the TS worklist).
  const reload = () => { loadExpectedBills(year).then(data => setRows(data)); if (onMutated) onMutated(); };

  // A parcel is actionable only when a bill exists and is awaiting CS review.
  const actionable = (p) => p.workflow_status === 'processed' && !!p.tax_bill_id;

  // ⛔ AN IMPLAUSIBLE DISCOUNT IS UNAPPROVABLE, not merely visible. Four bills were approved showing a
  // 39-million-dollar discount on a ~$397k bill: the figure was on screen (:20 column, :369 detail) and read
  // straight past. Displaying a wrong number is not a control; refusing to act on it is.
  // Two independent conditions, matching the DB CHECK in 2026-08-19_tax_bills_discount_guard.sql:
  //   • a discount on a bill whose own discount_available is not 'Yes' — impossible by definition
  //   • a discount above 25% of the bill — real early-pay discounts run 1-4%; anything near half is a scope
  //     or decimal error (see the TBT derivation note in api/generate-tbt.js)
  const DISCOUNT_MAX_FRACTION = 0.25;
  function discountProblem(b) {
    const d = Number(b?.discount_amount);
    if (!d) return null;
    const avail = String(b?.discount_available || '').trim().toLowerCase();
    if (avail !== 'yes') return `discount ${money(d)} stored on a bill flagged discount_available "${b?.discount_available ?? 'null'}"`;
    const tot = Number(b?.total_bill_amount);
    if (tot > 0 && d > tot * DISCOUNT_MAX_FRACTION) return `discount ${money(d)} is ${Math.round((d / tot) * 100)}% of the ${money(tot)} bill — real early-pay discounts are 1-4%`;
    return null;
  }

  // → { ok, approvedIds, filedPaid } . ⛔ A PAID BILL IS A HISTORICAL RECORD (catch-up years): approving it FILES it
  // — never 'approved', so it can never reach Transmittals or a TBT. Unpaid bills are approved exactly as before.
  async function approveIds(ids) {
    // Re-read the rows being approved: the grid carries expected_bills fields, not the full bill, and the
    // check must run against what is actually stored rather than what happens to be rendered.
    const { data: toCheck, error: readErr } = await supabase.from('tax_bills')
      .select('id, discount_amount, discount_available, total_bill_amount, account_number, paid_source').in('id', ids);
    if (readErr) { window.alert('Could not verify these bills before approving: ' + readErr.message); return { ok: false }; }
    // ⛔ NO AMOUNT DUE, NO APPROVAL. The transmittal's Total is Σ total_bill_amount; a bill without one would add
    // nothing and go out short. A scanned bill cannot be submitted blank, but a FLAGGED one can be stored blank.
    const noAmount = (toCheck || []).filter(b => b.total_bill_amount == null);
    if (noAmount.length) {
      window.alert(`Cannot approve ${noAmount.length} bill${noAmount.length > 1 ? 's' : ''} with no Amount Due:\n\n` +
        noAmount.map(b => `• ${b.account_number || b.id}`).join('\n') + '\n\nEnter the amount due exactly as printed (0 if paid) and try again.');
      return { ok: false };
    }
    const blocked = (toCheck || []).map(b => ({ b, why: discountProblem(b) })).filter(x => x.why);
    if (blocked.length) {
      window.alert(
        `Cannot approve ${blocked.length} bill${blocked.length > 1 ? 's' : ''} — the discount is not credible:\n\n` +
        blocked.map(x => `• ${x.b.account_number || x.b.id}: ${x.why}`).join('\n') +
        `\n\nCorrect the discount on the bill (or clear it) and try again.`);
      return { ok: false };
    }
    const paidIds = (toCheck || []).filter(b => b.paid_source).map(b => b.id);
    const unpaidIds = ids.filter(id => !paidIds.includes(id));
    const stamp = { approved_by: staffUser?.id || null, approved_at: new Date().toISOString() };
    for (const [list, status] of [[unpaidIds, TAX_BILL_STATUS.APPROVED], [paidIds, TAX_BILL_STATUS.FILED]]) {
      if (!list.length) continue;
      const { data: upd, error } = await supabase.from('tax_bills').update({ status, ...stamp }).in('id', list).select('id');
      if (error) { console.error('[approvals] approve:', error); window.alert('Approve failed: ' + (error.message || error)); return { ok: false }; }
      if ((upd || []).length !== list.length) { window.alert(`Approve incomplete — ${(upd || []).length} of ${list.length} bills were updated (blocked?). Refresh and check.`); return { ok: false }; }
    }
    return { ok: true, approvedIds: unpaidIds, filedPaid: paidIds.length };
  }

  // Approve one or many bills, then fork (non-transmit clients must choose File vs Transmittals — blocking).
  async function doApprove(ids, nonTransmit) {
    if (busy || !ids.length) return;
    setBusy(true);
    const res = await approveIds(ids);
    setBusy(false);
    if (!res.ok) return;
    reload();
    const paidNote = res.filedPaid ? `${res.filedPaid} paid bill${res.filedPaid > 1 ? 's' : ''} filed (never transmitted)` : '';
    const n = res.approvedIds.length;
    if (!n) { setToast(`Approved — ${paidNote}`); return; }
    if (nonTransmit) { setFileFork({ ids: res.approvedIds }); if (paidNote) setToast(paidNote); }   // unpaid stay 'approved' until the CS picks File or Transmittals
    else setToast(`Approved — ${n} bill${n > 1 ? 's' : ''} sent to Transmittals${paidNote ? ` · ${paidNote}` : ''}`);
  }

  async function forkFile() {
    if (busy || !fileFork) return;
    setBusy(true);
    const { error } = await supabase.from('tax_bills').update({ status: TAX_BILL_STATUS.FILED }).in('id', fileFork.ids);
    setBusy(false);
    if (error) { console.error('[approvals] file:', error); window.alert('File failed: ' + (error.message || error)); return; }
    const n = fileFork.ids.length; setFileFork(null); reload();
    setToast(`Filed — ${n} bill${n > 1 ? 's' : ''}`);
  }
  const forkTransmit = () => { const n = fileFork.ids.length; setFileFork(null); reload(); setToast(`Sent to Transmittals — ${n} bill${n > 1 ? 's' : ''}`); }; // leave 'approved' → Bubble 3 will pull it

  async function submitIssue(p) {
    const note = issueText.trim();
    if (busy || !note || !p.tax_bill_id) return;
    setBusy(true);
    const { error } = await supabase.from('tax_bills')
      .update({ status: TAX_BILL_STATUS.REJECTED, rejected_by: staffUser?.id || null, rejected_at: new Date().toISOString(), rejection_note: note })
      .eq('id', p.tax_bill_id);
    setBusy(false);
    if (error) { console.error('[approvals] issue:', error); window.alert('Issue failed: ' + (error.message || error)); return; }
    setIssueFor(null); setIssueText(''); reload();
  }

  return (
    <div style={{ width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px', flexWrap: 'wrap', gap: '10px' }}>
        <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '24px', fontWeight: '700', color: c.text }}>Tax Bill Approvals</div>
        <select value={year} onChange={e => setYear(e.target.value)} style={{ ...s.input, width: 'auto' }}>
          {years.map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>
      <div style={{ fontSize: '13px', color: c.muted, marginBottom: '16px' }}>Bills awaiting your approval for {year} — your clients are pinned to the top.</div>

      <div style={{ maxWidth: '360px', marginBottom: '14px' }}>
        <input style={s.input} placeholder="Search property, client, collector…" value={search} onChange={e => setSearch(e.target.value)} />
      </div>

      {loading
        ? <div style={{ color: c.muted, fontSize: '13px', padding: '20px 0' }}>Loading…</div>
        : filtered.length === 0
          ? <div style={{ color: c.muted, fontSize: '13px', padding: '20px 0' }}>{search ? 'No matching properties.' : 'Nothing awaiting approval.'}</div>
          : filtered.map(g => {
            const cs = csFor(g.client_id);
            return (
              <div key={g.property_id} style={{ background: c.card, border: `1px solid ${cs.mine ? c.mine : c.border}`, borderRadius: '12px', marginBottom: '14px', overflow: 'hidden' }}>
                {/* Property header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', padding: '12px 16px', borderBottom: `1px solid ${c.border}`, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '15px', fontWeight: '700', color: c.text }}>{g.property_name || '—'}</div>
                    <div style={{ fontSize: '12px', color: c.muted }}>{g.client_name || '—'}{g.state ? ` · ${g.state}` : ''}</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '12px', color: c.sub }}>CS: <span style={{ fontWeight: '700', color: cs.mine ? (isDark ? '#fca5a5' : 'var(--red)') : c.text }}>{cs.name || '—'}{cs.mine ? ' (you)' : ''}</span></span>
                    {g.awaiting > 0 && <span style={{ ...chip('processed', isDark) }}>{g.awaiting} awaiting</span>}
                  </div>
                </div>

                {/* (collector, installment) sub-cards */}
                <div style={{ padding: '10px 16px 14px' }}>
                  {g.subs.map(sub => { const approvable = sub.parcels.filter(actionable); return (
                    <div key={`${sub.collector_id}|${sub.installment_number ?? '-'}`} style={{ background: c.subCard, border: `1px solid ${c.border}`, borderRadius: '10px', padding: '10px 12px', marginTop: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '6px', flexWrap: 'wrap' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: c.text }}>
                          {sub.collector_name || '—'}
                          <span style={{ fontWeight: '500', color: c.muted }}> · {sub.installment_number != null ? `Installment ${sub.installment_number}` : 'Supplemental / other'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {sub.orphan && <span title={`The bill's collector is linked, but has no ${year} version/installments — roll the collector forward to ${year}. The bill can still be approved.`} style={{ ...chip('rejected', isDark) }}>⚠ no collector version for {year} — roll the collector forward</span>}
                          <span style={{ fontSize: '12px', color: c.muted }}>Approved {sub.approved}/{sub.withBill || 0}</span>
                          {approvable.length > 0 && <button style={busy ? disabled(s.btnApprove) : s.btnApprove} disabled={busy} onClick={() => doApprove(approvable.map(x => x.tax_bill_id), !sub.transmit_bills)}>✓ Approve all ({approvable.length})</button>}
                        </div>
                      </div>
                      {/* parcels */}
                      <div>
                        {sub.parcels.map((p, i) => (
                          <React.Fragment key={`${p.parcel_id}|${i}`}>
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', padding: '6px 2px', borderTop: i === 0 ? 'none' : `1px solid ${c.rowBorder}` }}>
                              <div style={{ fontSize: '12px', color: c.text, fontFamily: "'DM Mono',monospace" }}>
                                {(() => { const a = resolveAccount(p, acctFb); return <span title={accountTitle(a, year)}>{a.value || '—'}</span>; })()}{p.kind ? <span style={{ color: c.muted, fontFamily: "'Public Sans',sans-serif" }}> · {p.kind}</span> : null}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <span style={{ fontSize: '11px', color: c.muted }}>{fmtDate(p.due_date)}</span>
                                <span style={chip(p.workflow_status, isDark)}>{workflowLabel(p)}</span>
                                {billFlags[p.tax_bill_id]?.paid && <span title="Paid bill (catch-up year) — approving files it; it is never transmitted" style={{ fontSize: '10px', fontWeight: 800, color: '#059669', background: 'rgba(52,211,153,0.14)', borderRadius: '6px', padding: '2px 6px' }}>PAID</span>}
                                {/* The payment as submitted, read-only — approving approves the bill and the payment together. */}
                                {billFlags[p.tax_bill_id]?.payment && <span style={{ fontSize: '10.5px', color: c.muted }}>{paymentSummary(billFlags[p.tax_bill_id].payment)}</span>}
                                {billFlags[p.tax_bill_id]?.needsReview && <span title={billFlags[p.tax_bill_id].reviewNote || 'Needs review'} style={{ fontSize: '10px', fontWeight: 800, color: '#b45309', border: '1px solid rgba(180,83,9,0.45)', borderRadius: '6px', padding: '1px 6px' }}>⚠ review</span>}
                                {p.tax_bill_id && <button style={s.btnGhost} onClick={() => toggleReview(p.tax_bill_id)}>{openBill === p.tax_bill_id ? '▾ Review' : '▸ Review'}</button>}
                                {actionable(p) && (
                                  <>
                                    <button style={busy ? disabled(s.btnApprove) : s.btnApprove} disabled={busy} onClick={() => doApprove([p.tax_bill_id], !p.transmit_bills)}>✓ Approve</button>
                                    <button style={busy ? disabled(s.btnIssue) : s.btnIssue} disabled={busy} onClick={() => { setIssueFor(p.tax_bill_id); setIssueText(''); }}>Issue</button>
                                  </>
                                )}
                              </div>
                            </div>
                            {issueFor && issueFor === p.tax_bill_id && (
                              <div style={{ padding: '4px 2px 10px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                <textarea autoFocus value={issueText} onChange={e => setIssueText(e.target.value)}
                                  placeholder="What needs fixing? This comment goes back to the TS with the bill."
                                  style={{ ...s.input, width: '100%', minHeight: '56px', resize: 'vertical' }} />
                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                  <button style={s.btnGhost} onClick={() => { setIssueFor(null); setIssueText(''); }}>Cancel</button>
                                  <button style={(busy || !issueText.trim()) ? disabled(s.btnIssue) : s.btnIssue} disabled={busy || !issueText.trim()} onClick={() => submitIssue(p)}>↩ Send back to TS</button>
                                </div>
                              </div>
                            )}
                            {/* Review panel — the CS's second check: the county's PDF next to what the TS confirmed */}
                            {openBill === p.tax_bill_id && (() => {
                              const d = billData[p.tax_bill_id];
                              if (!d) return <div style={{ padding: '8px 2px', fontSize: '12px', color: c.muted }}>Loading review…</div>;
                              const ef = d.extracted_fields || {};
                              const lbl = { fontSize: '10px', fontWeight: 700, color: c.muted, textTransform: 'uppercase', letterSpacing: '0.04em' };
                              const val = { fontSize: '13px', color: c.text, fontFamily: "'DM Mono',monospace" };
                              const F = (label, v) => <div><div style={lbl}>{label}</div><div style={val}>{v}</div></div>;
                              const editing = !!editVals;   // this panel is the only one open, so editVals ⇒ editing THIS bill
                              const panelBg = isDark ? 'rgba(0,0,0,0.18)' : '#faf9f7';
                              return (
                                <div style={{ margin: '4px 2px 12px', border: `1px solid ${c.border}`, borderRadius: '10px', background: panelBg, overflow: 'hidden' }}>
                                  {/* header context */}
                                  <div style={{ padding: '8px 12px', borderBottom: `1px solid ${c.border}`, display: 'flex', flexWrap: 'wrap', gap: '4px 16px', fontSize: '11px', color: c.sub }}>
                                    <span><b style={{ color: c.text }}>Parcel</b> {ef.parcel_number || resolveAccount(p, acctFb).value || '—'}</span>
                                    <span><b style={{ color: c.text }}>Property</b> {g.property_name || '—'}</span>
                                    <span><b style={{ color: c.text }}>Account</b> {ef.account_number || resolveAccount(p, acctFb).value || '—'}</span>
                                    <span><b style={{ color: c.text }}>Year</b> {d.tax_year || year}</span>
                                    <span><b style={{ color: c.text }}>Installment</b> {d.bill_type || `#${p.installment_number}`}</span>
                                    <span><b style={{ color: c.text }}>Submitted by</b> {d.tsName || '—'}</span>
                                  </div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                                    {/* LEFT — the bill PDF (what the county sent). #navpanes=0 hides the browser
                                        PDF viewer's page-thumbnail strip; view=FitH fits the page to the width so
                                        the bill is legible without horizontal scrolling. Given the wider column. */}
                                    <div style={{ flex: '1.6 1 400px', minWidth: '320px', padding: '10px 12px', borderRight: `1px solid ${c.border}` }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: c.text }}>The bill (county)</div>
                                        {d.pdfUrl && <a href={d.pdfUrl} target="_blank" rel="noreferrer" style={{ fontSize: '11px', color: 'var(--red)', fontWeight: 600 }}>Open PDF ↗</a>}
                                      </div>
                                      {d.pdfUrl
                                        ? <iframe src={`${d.pdfUrl}#toolbar=1&navpanes=0&view=FitH`} title="Scanned tax bill" style={{ width: '100%', height: '560px', border: `1px solid ${c.border}`, borderRadius: '6px', background: '#fff' }} />
                                        : <div style={{ fontSize: '12px', color: c.muted, padding: '20px 0' }}>No scanned PDF on file for this bill.</div>}
                                    </div>
                                    {/* RIGHT — what the TS confirmed at Validate & Submit */}
                                    <div style={{ flex: '1 1 260px', minWidth: '260px', padding: '10px 12px' }}>
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                        <div style={{ fontSize: '12px', fontWeight: 700, color: c.text }}>TS-confirmed extraction</div>
                                        {actionable(p) && (editing ? (
                                          <div style={{ display: 'flex', gap: '6px' }}>
                                            <button style={busy ? disabled({ ...s.btnGhost, padding: '4px 10px' }) : { ...s.btnGhost, padding: '4px 10px' }} disabled={busy} onClick={() => setEditVals(null)}>Cancel</button>
                                            <button style={busy ? disabled({ ...s.btnApprove, padding: '4px 12px' }) : { ...s.btnApprove, padding: '4px 12px' }} disabled={busy} onClick={() => saveEdit(p.tax_bill_id)}>Save</button>
                                          </div>
                                        ) : (
                                          <button style={{ ...s.btnGhost, padding: '4px 10px' }} onClick={() => startEdit(d)} title="Correct a figure inline (identity/parcel errors → send back to TS)">✎ Edit</button>
                                        ))}
                                      </div>
                                      {editing ? (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                                          {CS_EDIT_FIELDS.map(f => (
                                            <div key={f.key}>
                                              <div style={lbl}>{f.label}</div>
                                              <input type={f.type === 'date' ? 'date' : 'text'} value={editVals[f.key] ?? ''}
                                                onChange={e => setEditVals(v => ({ ...v, [f.key]: e.target.value }))}
                                                style={{ ...s.input, padding: '5px 8px', fontSize: '12px' }} />
                                            </div>
                                          ))}
                                          {F('Payee / Collector', ef.collector || p.collector_number || '—')}
                                          {F('Owner', ef.owner_name || '—')}
                                        </div>
                                      ) : (
                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 12px' }}>
                                          {F('Amount Due (this bill)', <b style={{ color: 'var(--red)' }}>{money(d.total_bill_amount)}</b>)}
                                          {F('Ad Valorem (basis)', money(d.total_ad_valorem))}
                                          {F('Non-Ad-Valorem', money(d.total_non_ad_valorem))}
                                          {F('Discount', d.discount_amount != null ? money(d.discount_amount) : '—')}
                                          {F('Penalty / Fees', money(d.penalty_fees))}
                                          {F('Taxable Value', d.taxable_value != null ? money(d.taxable_value) : '—')}
                                          {F('Market Value', d.market_value != null ? money(d.market_value) : '—')}
                                          {F('Tax Rate', d.total_tax_rate != null ? d.total_tax_rate + '%' : '—')}
                                          {F('Due Date', fmtDate(d.due_date))}
                                          {F('Payee / Collector', ef.collector || p.collector_number || '—')}
                                          {F('Owner', ef.owner_name || '—')}
                                          {F('Discount Date', fmtDate(d.discount_date))}
                                        </div>
                                      )}
                                      {d.cs_edits && Object.keys(d.cs_edits).length > 0 && (
                                        <div style={{ marginTop: '10px', borderTop: `1px solid ${c.border}`, paddingTop: '8px' }}>
                                          <div style={{ fontSize: '11px', fontWeight: 700, color: c.muted, marginBottom: '4px' }}>
                                            CS corrections{d.edited_by ? ` · ${staffNameFrom(staff, d.edited_by)}` : ''}{d.edited_at ? ` · ${fmtDate(String(d.edited_at).slice(0, 10))}` : ''}
                                          </div>
                                          {Object.entries(d.cs_edits).map(([k, e]) => {
                                            const f = CS_EDIT_FIELDS.find(x => x.key === k);
                                            const fv = (x) => !f ? String(x) : f.type === 'date' ? fmtDate(x) : f.type === 'rate' ? (x == null ? '—' : x + '%') : money(x);
                                            return <div key={k} style={{ fontSize: '11px', color: c.sub, marginBottom: '2px' }}>{f ? f.label : k}: <span style={{ textDecoration: 'line-through', opacity: 0.7 }}>TS {fv(e.from)}</span> → <b style={{ color: c.text }}>CS {fv(e.to)}</b></div>;
                                          })}
                                        </div>
                                      )}
                                      {ef.notes && <div style={{ marginTop: '10px', fontSize: '11px', color: c.sub, lineHeight: 1.45, borderTop: `1px solid ${c.border}`, paddingTop: '8px' }}><b style={{ color: c.text }}>Scan notes:</b> {ef.notes}</div>}
                                      {Array.isArray(d.jurisdiction_rates) && d.jurisdiction_rates.length > 0 && (
                                        <div style={{ marginTop: '10px', borderTop: `1px solid ${c.border}`, paddingTop: '8px' }}>
                                          <div style={{ fontSize: '11px', fontWeight: 700, color: c.muted, marginBottom: '6px' }}>Jurisdiction breakdown</div>
                                          <JurisdictionBreakdown rates={d.jurisdiction_rates} unit={detectRateUnit(d.jurisdiction_rates, d.total_ad_valorem, d.taxable_value)} darkMode={isDark} />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                              );
                            })()}
                          </React.Fragment>
                        ))}
                      </div>
                    </div>
                  ); })}
                </div>
              </div>
            );
          })}

      {/* Blocking File / Transmittals fork — non-transmit client, after approve. No dismiss; the CS must choose. */}
      {fileFork && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }}>
          <div style={{ background: c.card, border: `1px solid ${c.border}`, borderRadius: '14px', padding: '22px 24px', maxWidth: '460px', width: '100%' }}>
            <div style={{ fontFamily: "'Archivo',sans-serif", fontSize: '18px', fontWeight: '700', color: c.text, marginBottom: '8px' }}>{fileFork.ids.length > 1 ? `Approve these ${fileFork.ids.length} bills` : 'Approve this bill'}</div>
            <div style={{ fontSize: '13px', color: c.muted, marginBottom: '18px', lineHeight: 1.5 }}>
              This client does not receive transmittals. Choose how to finalize {fileFork.ids.length > 1 ? `these ${fileFork.ids.length} approved bills` : 'this approved bill'} — you must pick one.
            </div>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              <button style={busy ? disabled({ ...s.btnPrimary, background: 'linear-gradient(135deg,#0e7490,#0c6076)' }) : { ...s.btnPrimary, background: 'linear-gradient(135deg,#0e7490,#0c6076)' }} disabled={busy} onClick={forkTransmit}>📤 Approve &amp; Send to Transmittals</button>
              <button style={busy ? disabled(s.btnPrimary) : s.btnPrimary} disabled={busy} onClick={forkFile}>📁 Approve &amp; File</button>
            </div>
          </div>
        </div>
      )}

      {/* Transient confirmation banner — states the outcome of an approve / file / transmit action */}
      {toast && (
        <div style={{ position: 'fixed', bottom: '24px', left: '50%', transform: 'translateX(-50%)', background: '#059669', color: '#fff', padding: '11px 20px', borderRadius: '10px', fontSize: '13px', fontWeight: '700', boxShadow: '0 6px 20px rgba(0,0,0,0.25)', zIndex: 1100, fontFamily: fontStack }}>
          ✓ {toast}
        </div>
      )}
    </div>
  );
}
